import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

type DragContext = {
  isDragging: boolean
  startClientX: number
  startScrollLeft: number
  maxAbsDeltaX: number
}

type ClickHandler = (() => void) | undefined

const DRAG_SUPPRESS_CLICK_THRESHOLD_PX = 6
const DRAG_SUPPRESS_CLICK_WINDOW_MS = 180

export function useDraggableHorizontalScroll<T extends HTMLElement>(): [
  RefObject<T | null>,
  boolean,
  (handler: ClickHandler) => ClickHandler,
] {
  const containerRef = useRef<T>(null)
  const [isDragging, setIsDragging] = useState(false)
  const suppressClickUntilRef = useRef(0)

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }

    const drag: DragContext = {
      isDragging: false,
      startClientX: 0,
      startScrollLeft: 0,
      maxAbsDeltaX: 0,
    }

    const stopDragging = () => {
      if (!drag.isDragging) {
        return
      }

      if (drag.maxAbsDeltaX >= DRAG_SUPPRESS_CLICK_THRESHOLD_PX) {
        suppressClickUntilRef.current = Date.now() + DRAG_SUPPRESS_CLICK_WINDOW_MS
      }

      drag.isDragging = false
      setIsDragging(false)
      document.body.style.removeProperty('user-select')
    }

    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) {
        return
      }

      drag.isDragging = true
      drag.startClientX = event.clientX
      drag.startScrollLeft = element.scrollLeft
      drag.maxAbsDeltaX = 0
      setIsDragging(true)
      document.body.style.setProperty('user-select', 'none')
      event.preventDefault()
    }

    const onMouseMove = (event: MouseEvent) => {
      if (!drag.isDragging) {
        return
      }

      const deltaX = event.clientX - drag.startClientX
      drag.maxAbsDeltaX = Math.max(drag.maxAbsDeltaX, Math.abs(deltaX))
      element.scrollLeft = drag.startScrollLeft - deltaX
    }

    const preventImageDrag = (event: DragEvent) => {
      event.preventDefault()
    }

    element.addEventListener('mousedown', onMouseDown)
    element.addEventListener('dragstart', preventImageDrag)
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', stopDragging)
    window.addEventListener('blur', stopDragging)

    return () => {
      element.removeEventListener('mousedown', onMouseDown)
      element.removeEventListener('dragstart', preventImageDrag)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', stopDragging)
      window.removeEventListener('blur', stopDragging)
      document.body.style.removeProperty('user-select')
    }
  }, [])

  const bindDragAwareClick = (handler: ClickHandler): ClickHandler => {
    if (!handler) {
      return undefined
    }

    return () => {
      if (Date.now() < suppressClickUntilRef.current) {
        return
      }
      handler()
    }
  }

  return [containerRef, isDragging, bindDragAwareClick]
}
